import React, { useMemo, useState } from "react";
import { ParseResult } from "../../../lib/csvUtils";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CardDefaultFields, { DefaultsChangedEvent } from "./CardDefaultFields";
import CardDataMapper from "./CardDataMapper";
import { Bin, Data } from "../../../store/card/types";
import { saveRequest } from "../../../store/card/actions";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "../../../store/workspace/workspaceSlice";
import { MessageStatus } from "../../../store/workspace/types";
import { ApplicationState } from "../../../store";
import INTERNATIONAL_BINS from "../../../common/config/internationalBINs.json"
interface Props {
  cardTableName: string;
  parsedResult: ParseResult | null;
  onCancel: () => void;
}

const CardImportResults = ({
  cardTableName,
  parsedResult,
  onCancel,
}: Props) => {
  const [name, setName] = useState(cardTableName);
  const [PINBlock, setPINBlock] = useState("Format 0");
  const [DUKPT, setDUKPT] = useState(true);
  const [TippingEnabled, setTippingenabled] = useState(true);
  const history = useHistory();
  const dispatch = useDispatch();
  const submitting = useSelector<ApplicationState, boolean>(
    (store) => store.card.loading
  );

  const payload = useMemo(() => {
    const cardDataMapper = new CardDataMapper(cardTableName);
    INTERNATIONAL_BINS?.forEach((entry) => {
      cardDataMapper.accept(entry);
    });
    parsedResult?.data.forEach((entry) => {
      cardDataMapper.accept(entry);
    });

    // @ts-ignore
    return cardDataMapper.collect(DUKPT, PINBlock, TippingEnabled);
  }, [INTERNATIONAL_BINS, parsedResult, cardTableName]);

  const cardTable = useMemo(
    () => ({
      header: ["Card Issuer", "Card Type", "BIN", "Length"],
      data: payload.attributes.CardBin.map((cardBin) => ({
        "Card Issuer": cardBin.DescLong,
        "Card Type": cardBin.DescShort,
        BIN: cardBin.Bin,
      })),
    }),
    [payload]
  );

  const onSubmit = (payload: Data) => {
    payload.attributes.Name = name;
    payload.attributes?.CardBin?.forEach((binEntry) => {
      binEntry.DUKPT = DUKPT;
      binEntry.PINBlock = PINBlock;
    });
    dispatch(
      saveRequest(payload, (err, data) => {
        if (err) {
          dispatch(
            notify({
              status: MessageStatus.error,
              message: "Failed to save card prefix table",
            })
          );
        } else {
          dispatch(
            notify({
              status: MessageStatus.success,
              message: " Card prefix table saved",
            })
          );
          history.push("/card");
        }
      })
    );
  };

  return (
    <>
      <Box mb={2}>
        <Paper>
          <Box p={1}>
            <Box mb={2}>
              <CardDefaultFields
                defaultName={cardTableName}
                onChange={(event: DefaultsChangedEvent) => {
                  const { Name, PINBlock, DUKPT } = event.values;
                  setName(Name);
                  setPINBlock(PINBlock);
                  setDUKPT(DUKPT);
                  setTippingenabled(TippingEnabled);
                }}
              />
            </Box>
            <Box my={1}>
              <Box width="100%" display="flex" flexDirection="row-reverse">
                <Box mx={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    onClick={() => {
                      onSubmit((payload as unknown) as Data);
                    }}
                  >
                    Submit
                  </Button>
                </Box>
                <Box mx={1}>
                  <Button
                    variant="contained"
                    disabled={submitting}
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
      <Box mb={2}>
        <Paper>
          <Box p={1}>
            <h4>Preview</h4>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                {cardTable?.header?.map((cellValue?: string) => (
                  <TableCell key={cellValue}>{cellValue}</TableCell>
                ))}
              </TableHead>
              <TableBody>
                {cardTable?.data.map((cardBin: any, index) => (
                  <TableRow key={index}>
                    {cardTable?.header?.map(
                      (headerValue: string | undefined) => {
                        if (headerValue) {
                          let value = cardBin[headerValue];

                          if (headerValue === "BIN") {
                            value = cardBin.BIN.map(
                              (binEntry: Bin) =>
                                `${binEntry.From} - ${binEntry.To}`
                            );
                          } else if (headerValue === "Length") {
                            value = cardBin.BIN.map(
                              (binEntry: Bin) => binEntry.Length
                            );
                          }
                          if (Array.isArray(value)) {
                            return (
                              <TableCell key={headerValue} style={{ verticalAlign: "top" }}>
                                <table>
                                  <tbody>
                                    {value.map((entry) => (
                                      <tr key={entry}>
                                        <td>{entry}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </TableCell>
                            );
                          } else {
                            return (
                              <TableCell key={headerValue} style={{ verticalAlign: "top" }}>
                                {value}
                              </TableCell>
                            );
                          }
                        } else {
                          return null;
                        }
                      }
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </>
  );
};

export default CardImportResults;
